import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Trash2, Edit, Plus, Eye, EyeOff, Lock, LogOut } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  
  const [posts, setPosts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    featured_image_url: '',
    status: 'draft',
    faq: []
  });

  // Check if already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setAdminToken(token);
      setIsAuthenticated(true);
      fetchPosts();
    }
  }, []);

  // Fetch posts when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        password: password
      });
      
      if (response.data.error) {
        toast.error('Invalid password');
        return;
      }
      
      const token = response.data.token;
      setAdminToken(token);
      localStorage.setItem('adminToken', token);
      setIsAuthenticated(true);
      setPassword('');
      toast.success('Login successful!');
    } catch (error) {
      toast.error('Login failed. Please check your password.');
      console.error('Login error:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken('');
    setIsAuthenticated(false);
    setPosts([]);
    toast.success('Logged out successfully');
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to fetch posts');
      console.error('Fetch error:', error);
    }
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const headers = {
        'Authorization': `Bearer ${adminToken}`
      };
      
      if (isEditing && currentPost) {
        // Update existing post
        await axios.put(`${API_URL}/api/posts/${currentPost.id}`, formData, { headers });
        toast.success('Post updated successfully!');
      } else {
        // Create new post
        await axios.post(`${API_URL}/api/posts`, formData, { headers });
        toast.success('Post created successfully!');
      }
      
      // Reset form and fetch updated posts
      resetForm();
      fetchPosts();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error(isEditing ? 'Failed to update post' : 'Failed to create post');
      }
      console.error('Save error:', error);
    }
  };

  const handleEdit = (post) => {
    setIsEditing(true);
    setCurrentPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      author: post.author,
      featured_image_url: post.featured_image_url,
      status: post.status,
      faq: post.faq || []
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }
    
    try {
      const headers = {
        'Authorization': `Bearer ${adminToken}`
      };
      await axios.delete(`${API_URL}/api/posts/${postId}`, { headers });
      toast.success('Post deleted successfully!');
      fetchPosts();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to delete post');
      }
      console.error('Delete error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      author: '',
      featured_image_url: '',
      status: 'draft',
      faq: []
    });
    setIsEditing(false);
    setCurrentPost(null);
  };

  // FAQ Management Functions
  const addFAQItem = () => {
    setFormData({
      ...formData,
      faq: [...formData.faq, { question: '', answer: '' }]
    });
  };

  const removeFAQItem = (index) => {
    const newFaq = formData.faq.filter((_, i) => i !== index);
    setFormData({ ...formData, faq: newFaq });
  };

  const updateFAQItem = (index, field, value) => {
    const newFaq = [...formData.faq];
    newFaq[index][field] = value;
    setFormData({ ...formData, faq: newFaq });
  };

  // Function to parse and format the blog content (similar to ServiceDetail.jsx)
  const formatBlogContent = (content) => {
    const sections = content.split('\n\n');
    
    return sections.map((section, index) => {
      const lines = section.split('\n').filter(line => line.trim());
      if (lines.length === 0) return null;
      
      const firstLine = lines[0];
      
      // Check if line is a heading (starts with ##)
      if (firstLine.startsWith('##')) {
        const heading = firstLine.replace(/^##\s*/, '');
        return (
          <h2 key={index} className="text-2xl font-bold mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>
            {heading}
          </h2>
        );
      }
      
      // Check if ALL lines in this section are bullet points (start with *)
      const allBullets = lines.every(line => line.trim().startsWith('*'));
      if (allBullets) {
        return (
          <div key={index} className="mb-4">
            {lines.map((line, lineIndex) => (
              <div key={lineIndex} className="flex items-start gap-3 mb-2">
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--accent-primary)' }}></div>
                <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {line.replace(/^\*\s*/, '')}
                </p>
              </div>
            ))}
          </div>
        );
      }
      
      // Regular paragraph
      return (
        <p key={index} className="text-base leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
          {section}
        </p>
      );
    });
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" style={{ paddingTop: '80px' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-primary)' }}>
              <Lock className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2" style={{ color: 'var(--text-primary)' }}>
            Blog Admin
          </h1>
          <p className="text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
            Enter password to access blog management
          </p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Admin Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              className="w-full py-3 rounded-lg text-white font-semibold transition-all hover:opacity-90"
              style={{ background: 'var(--accent-primary)' }}
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingTop: '80px', paddingBottom: '40px' }}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Blog Management
          </h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Create/Edit Form */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          
          <form onSubmit={handleCreateOrUpdate} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter blog post title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Author *
              </label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter author name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Featured Image URL
              </label>
              <input
                type="url"
                value={formData.featured_image_url}
                onChange={(e) => setFormData({ ...formData, featured_image_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="https://example.com/image.jpg"
              />
              {formData.featured_image_url && (
                <div className="mt-4">
                  <img
                    src={formData.featured_image_url}
                    alt="Featured preview"
                    className="max-w-xs rounded-lg border border-gray-200"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Content *
              </label>
              <div className="mb-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <strong>Formatting Tips:</strong>
                <ul className="list-disc list-inside mt-1">
                  <li>Use <code>## Heading</code> for section headings</li>
                  <li>Use <code>* Item</code> for bullet points</li>
                  <li>Leave blank lines between paragraphs</li>
                </ul>
              </div>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter blog post content&#10;&#10;## Introduction&#10;&#10;Your content here...&#10;&#10;* Bullet point 1&#10;* Bullet point 2"
                rows="10"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            {/* FAQ Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <label className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    FAQ Section (Optional)
                  </label>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Add frequently asked questions to boost SEO and voice search rankings
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addFAQItem}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green-500 text-green-600 hover:bg-green-50 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>

              {formData.faq.length > 0 && (
                <div className="space-y-4">
                  {formData.faq.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          Question {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFAQItem(index)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove question"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={item.question}
                          onChange={(e) => updateFAQItem(index, 'question', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter question (e.g., How long does treatment take?)"
                        />
                        <textarea
                          value={item.answer}
                          onChange={(e) => updateFAQItem(index, 'answer', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter answer"
                          rows="3"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.faq.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    No FAQ items yet. Click "Add Question" to get started.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-3 rounded-lg text-white font-semibold transition-all hover:opacity-90"
                style={{ background: 'var(--accent-primary)' }}
              >
                {isEditing ? 'Update Post' : 'Create Post'}
              </button>
              
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 rounded-lg border border-gray-300 font-semibold transition-all hover:bg-gray-50"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Posts List */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              All Blog Posts ({posts.length})
            </h2>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
              <p className="text-lg">No blog posts yet. Create your first post above!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                          {post.title}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            post.status === 'published'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {post.status.toUpperCase()}
                        </span>
                      </div>
                      
                      <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                        By {post.author} • {new Date(post.created_at).toLocaleDateString()}
                      </p>
                      
                      {post.featured_image_url && (
                        <img
                          src={post.featured_image_url}
                          alt={post.title}
                          className="w-48 h-32 object-cover rounded-lg mb-3"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      
                      <div className="text-sm line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                        {post.content.substring(0, 200)}...
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(post)}
                        className="p-2 rounded-lg transition-all hover:bg-blue-50"
                        style={{ color: 'var(--accent-primary)' }}
                        title="Edit post"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-2 text-red-500 rounded-lg transition-all hover:bg-red-50"
                        title="Delete post"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogAdmin;
